import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { LocalLibrary, AccountBalanceWallet, StarOutlined } from '@mui/icons-material';

const VALUE_PROPS = [
  {
    key: 'seats',
    icon: <LocalLibrary sx={{ fontSize: 28 }} />,
    headline: '12,000+ seats',
    sub: 'across India',
  },
  {
    key: 'wallet',
    icon: <AccountBalanceWallet sx={{ fontSize: 28 }} />,
    headline: 'Pay with wallet',
    sub: 'instant top-ups',
  },
  {
    key: 'coins',
    icon: <StarOutlined sx={{ fontSize: 28 }} />,
    headline: 'Earn BML coins',
    sub: 'study & get rewarded',
  },
];

const INTERVAL_MS = 4000;

/**
 * AuthMarqueeStrip
 * Desktop-only (≥lg) translucent side strip cycling through 3 value props.
 * Reduced-motion: renders only the first item statically.
 */
function AuthMarqueeStrip() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const reduced = useReducedMotionPreferred();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % VALUE_PROPS.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [reduced]);

  const current = VALUE_PROPS[reduced ? 0 : index];

  const bgColor = isLight
    ? 'rgba(255,255,255,0.32)'
    : 'rgba(27,54,93,0.28)';

  const borderColor = isLight
    ? 'rgba(242,101,34,0.18)'
    : 'rgba(125,164,217,0.22)';

  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: 200,
        flexShrink: 0,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: theme.radius?.xl ?? 24,
        px: 3,
        py: 4,
        ml: 3,
        gap: 2,
        overflow: 'hidden',
      }}
      aria-hidden="true"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={current.key}
          initial={reduced ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? {} : { opacity: 0, y: -10 }}
          transition={{ duration: 0.28, ease: 'easeInOut' }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            width: '100%',
          }}
        >
          <Box sx={{ color: 'primary.main' }}>
            {current.icon}
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.primary"
              sx={{ lineHeight: 1.2 }}
            >
              {current.headline}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              {current.sub}
            </Typography>
          </Box>
        </motion.div>
      </AnimatePresence>

      {/* Progress dots */}
      {!reduced && (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
          {VALUE_PROPS.map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i === index ? 16 : 6,
                height: 6,
                borderRadius: 999,
                bgcolor: i === index ? 'primary.main' : 'text.tertiary',
                transition: 'width 0.3s ease, background-color 0.3s ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default AuthMarqueeStrip;
